import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { NotebookPen, ChevronDown, ChevronUp, X, Pin, CalendarClock } from "lucide-react";

const KIND_LABELS = { update: "Mise à jour", new_module: "Nouveau module", info: "Information", maintenance: "Maintenance" };
const KIND_COLORS = { update: "bg-[#002FA7] text-white", new_module: "bg-[#059669] text-white", info: "bg-[#0A0A0A] text-white", maintenance: "bg-[#E60000] text-white" };
const DISMISS_KEY = "pag-notepad-dismissed";
const readDismissed = () => { try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]"); } catch { return []; } };

// Phase 84 — Bloc-note « Prochaines mises à jour » : `mode="app"` (espaces pro, authentifié, repliable et fermable)
// ou `mode="signup"` / `mode="landing"` (public). Les notes fermées ne réapparaissent pas (localStorage).
export function UpdatesNotepad({ mode = "app", className = "", compact = false }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(() => localStorage.getItem("pag-notepad-collapsed") !== "1");
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    const req = mode === "app" ? api.get("/announcements") : api.get(`/public/announcements?placement=${mode === "signup" ? "signup" : "landing"}`);
    req.then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, [mode]);

  const visible = items.filter((a) => mode !== "app" || !dismissed.includes(a.id));
  if (!visible.length) return null;
  const dismiss = (id) => { const next = [...dismissed, id]; setDismissed(next); try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch { /* ignore */ } };
  const toggle = () => { const next = !open; setOpen(next); try { localStorage.setItem("pag-notepad-collapsed", next ? "0" : "1"); } catch { /* ignore */ } };

  return (
    <section className={`border border-[#0A0A0A] bg-[#FFFDF2] ${className}`} data-testid={`updates-notepad-${mode}`}>
      <button type="button" onClick={toggle} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid="updates-notepad-toggle">
        <span className="h-8 w-8 flex items-center justify-center bg-[#FFD700] text-[#0A0A0A]"><NotebookPen className="h-4 w-4" /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-[10px] uppercase tracking-[0.3em] font-bold text-[#002FA7]">Bloc-note PAG</span>
          <span className="block font-display font-bold text-sm sm:text-base truncate">Prochaines mises à jour & nouveautés <span className="text-[#868E96] font-normal">({visible.length})</span></span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-[#495057]" /> : <ChevronDown className="h-4 w-4 text-[#495057]" />}
      </button>
      {open && (
        <ul className={`border-t border-[#DEE2E6] divide-y divide-[#DEE2E6] ${compact ? "max-h-72 overflow-y-auto" : ""}`} data-testid="updates-notepad-list">
          {visible.map((a) => (
            <li key={a.id} className="px-4 py-3 flex gap-3" data-testid={`notepad-item-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {a.pinned && <Pin className="h-3 w-3 text-[#E60000]" />}
                  <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${KIND_COLORS[a.kind] || KIND_COLORS.update}`}>{KIND_LABELS[a.kind] || "Mise à jour"}</span>
                  {a.period && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#B45309]"><CalendarClock className="h-3 w-3" /> {a.period}</span>}
                </div>
                <div className="font-bold text-sm">{a.title}</div>
                <p className={`text-xs text-[#495057] mt-0.5 whitespace-pre-wrap ${compact ? "line-clamp-3" : ""}`}>{a.content}</p>
              </div>
              {mode === "app" && <button type="button" onClick={() => dismiss(a.id)} className="h-7 w-7 shrink-0 flex items-center justify-center text-[#868E96] hover:text-[#0A0A0A]" aria-label="Fermer" data-testid={`notepad-dismiss-${a.id}`}><X className="h-3.5 w-3.5" /></button>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
